/**
 * The match's quality level and what we know about its data — the
 * published quality overlay (app/layerstack/serve/quality.py, written by
 * serve-quality) via GET /api/v1/opsqa/games/<id>/quality.
 *
 * Each area says what was measured and, where it falls short, what that
 * costs downstream — so a "0 shots" reads as the feed's limit, not a
 * quiet match.
 */
import { useEffect, useState } from 'react'
import { api } from '../lib/api'

export interface QualityMeasures {
  integrity_ok: boolean | null
  vendor: string | null
  ball_accounted_pct: number | null
  ball_height_sd_m: number | null
  centre_spot_parks: number | null
  max_tracks_per_team: number | null
  periods: number | null
  pitch_assumed: boolean | null
  possessions: number | null
  shots: number | null
  goals: number | null
  failed_steps: string | null
}

export interface QualityThresholds {
  vendors: string[]
  min_ball_accounted_pct: number
  min_ball_height_sd_m: number
  max_tracks_per_team: number
  centre_park_s: number
}

export interface QualityPayload {
  game_id: number
  level: string | null
  reasons: string[]
  measures: QualityMeasures | null
  thresholds: QualityThresholds
  /** the overlay predates columns it should carry: rebuild, not a gap */
  stale?: boolean
  error?: string
}

export type AreaStatus = 'known' | 'partial' | 'gap'

export interface Area {
  area: string
  status: AreaStatus
  value: string
  /** what a gap costs downstream */
  cost?: string
}

/** The areas the page lists, from the measurements and their bars. */
export function qualityAreas(m: QualityMeasures, t: QualityThresholds): Area[] {
  const out: Area[] = []
  const vendor = m.vendor ?? null
  out.push(vendor
    ? { area: 'Tracking feed', value: vendor,
        status: vendor.split(',').every((v) => t.vendors.includes(v)) ? 'known' : 'partial',
        cost: vendor.split(',').every((v) => t.vendors.includes(v)) ? undefined
          : `only ${t.vendors.join(' and ')} feeds can reach Checked` }
    : { area: 'Tracking feed', value: 'none loaded', status: 'gap' })
  const acc = m.ball_accounted_pct
  out.push(acc == null
    ? { area: 'Ball tracking', value: 'not measured', status: 'gap' }
    : acc >= t.min_ball_accounted_pct
      ? { area: 'Ball tracking', value: `accounted for in ${acc}% of frames`, status: 'known' }
      : { area: 'Ball tracking', value: `accounted for in ${acc}% of frames`, status: 'gap',
          cost: 'the ball disappears instead of going out of play — shots and restarts are missed' })
  const sd = m.ball_height_sd_m
  if (acc != null) {
    out.push(sd == null
      ? { area: 'Ball height', value: 'not measured', status: 'gap' }
      : sd >= t.min_ball_height_sd_m
        ? { area: 'Ball height', value: `varies (sd ${sd} m)`, status: 'known' }
        : { area: 'Ball height', value: 'flat — no height in the feed', status: 'gap',
            cost: "on target vs over the bar, and headers, can't be told apart" })
  }
  const parks = m.centre_spot_parks
  if (parks != null) {
    out.push(parks === 0
      ? { area: 'Missing-ball placeholder', value: 'none', status: 'known' }
      : { area: 'Missing-ball placeholder', status: 'gap',
          value: `parked on the centre spot ${parks}× (≥ ${t.centre_park_s} s)`,
          cost: "kick-offs can't confirm goals" })
  }
  const tracks = m.max_tracks_per_team
  if (tracks != null) {
    out.push(tracks <= t.max_tracks_per_team
      ? { area: 'Player tracks', value: `${tracks} per team at most`, status: 'known' }
      : { area: 'Player tracks', value: `${tracks} for one team`, status: 'gap',
          cost: 'players’ tracks break up — who touched the ball is weak' })
  }
  const periods = m.periods
  out.push(periods == null
    ? { area: 'Periods', value: 'not measured', status: 'gap' }
    : periods >= 2
      ? { area: 'Periods', value: `${periods}`, status: 'known' }
      : { area: 'Periods', value: `${periods}`, status: 'gap', cost: 'halves and directions are unknown' })
  if (m.pitch_assumed != null) {
    out.push(m.pitch_assumed
      ? { area: 'Pitch size', value: 'platform default', status: 'partial',
          cost: 'distances and zones assume a standard pitch' }
      : { area: 'Pitch size', value: 'declared or measured', status: 'known' })
  }
  if (m.shots != null) {
    // Shots are not a quality bar: on a feed whose ball is fully tracked
    // with height, 0 may be the match; where the ball is not, the
    // detector cannot see them.
    const blind = out.some((a) => (a.area === 'Ball tracking' || a.area === 'Ball height')
      && a.status === 'gap')
    out.push(m.shots > 0
      ? { area: 'Shots and goals', value: `${m.shots} shots, ${m.goals ?? 0} goals`, status: 'known' }
      : blind
        ? { area: 'Shots and goals', value: '0 shots detected', status: 'gap',
            cost: "the detector can't see shots on this feed — chances, xG and the score are empty" }
        : { area: 'Shots and goals', value: '0 shots detected', status: 'partial',
            cost: 'check the match had none' })
  }
  out.push(m.failed_steps
    ? { area: 'Layer checks', value: `latest run failed: ${m.failed_steps}`, status: 'gap' }
    : m.integrity_ok
      ? { area: 'Layer checks', value: 'every step’s latest run succeeded', status: 'known' }
      : { area: 'Layer checks', value: 'no build record', status: 'partial' })
  return out
}

const STATUS_COLOR: Record<AreaStatus, string> = {
  known: '#22c55e', partial: '#eab308', gap: '#ef4444',
}
const STATUS_TEXT: Record<AreaStatus, string> = {
  known: 'Known', partial: 'Partial', gap: 'Gap',
}
const LEVEL_COLOR: Record<string, string> = {
  Certified: '#22c55e', Checked: '#3b82f6', Provisional: '#eab308',
}

export function MatchQuality({ gameId, refreshKey }: { gameId: number; refreshKey?: number }) {
  const [q, setQ] = useState<QualityPayload | null>(null)
  const [failed, setFailed] = useState<string | null>(null)
  useEffect(() => {
    let live = true
    setQ(null); setFailed(null)
    api<QualityPayload>(`/api/v1/opsqa/games/${gameId}/quality`)
      .then((d) => { if (live) setQ(d) })
      .catch((e: unknown) => { if (live) setFailed(String(e)) })
    return () => { live = false }
  }, [gameId, refreshKey])
  if (failed) return null
  if (!q) return null
  const areas = q.measures ? qualityAreas(q.measures, q.thresholds) : []
  return (
    <section data-testid="match-quality"
             style={{ border: '1px solid var(--color-border, #333)', borderRadius: '6px',
                      padding: '8px 12px', marginBottom: 'var(--space-2)', fontSize: '12px' }}>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
        <strong>Quality</strong>
        {q.level
          ? <span data-testid="quality-level"
                  style={{ padding: '1px 8px', borderRadius: '10px', fontWeight: 600,
                           color: '#fff', background: LEVEL_COLOR[q.level] ?? '#6b7280' }}>
              {q.level}
            </span>
          : q.stale
            ? <span className="muted">built before quality was measured — rebuild Serve Overlays</span>
            : <span className="muted">not measured yet — build Serve Overlays</span>}
        {q.level && q.reasons.length > 0 && (
          <span className="muted">not Checked: {q.reasons.join('; ')}</span>
        )}
        {q.error && <span className="muted">unreadable: {q.error}</span>}
      </div>
      {areas.length > 0 && (
        <table style={{ marginTop: '6px', borderCollapse: 'collapse', width: '100%' }}>
          <caption style={{ textAlign: 'left', fontWeight: 600, padding: '2px 0' }}>
            What we know about this match
          </caption>
          <tbody>
            {areas.map((a) => (
              <tr key={a.area}>
                <td style={{ padding: '2px 8px 2px 0', whiteSpace: 'nowrap' }}>{a.area}</td>
                <td style={{ padding: '2px 8px', whiteSpace: 'nowrap' }}>
                  <span aria-hidden style={{ display: 'inline-block', width: 8, height: 8,
                                             borderRadius: 4, marginRight: 4,
                                             background: STATUS_COLOR[a.status] }} />
                  {STATUS_TEXT[a.status]}
                </td>
                <td style={{ padding: '2px 8px' }}>{a.value}</td>
                <td className="muted" style={{ padding: '2px 0 2px 8px' }}>{a.cost ?? ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
}
